import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";

interface ListParams {
  page?: number;
  pageSize?: number;
  search?: string;
  status?: string;
}

export function useAdvertisers(params: ListParams = {}) {
  const { page = 1, pageSize = 50, search, status } = params;

  return useQuery({
    queryKey: ["advertisers", { page, pageSize, search, status }],
    queryFn: async () => {
      const qp: Record<string, string> = {
        page: String(page),
        pageSize: String(pageSize),
      };
      if (search) qp.search = search;
      if (status) qp.status = status;

      const res = await api.get("/v1/advertisers", qp);
      if (!res.ok) throw new Error("Failed to fetch advertisers");
      return res.json();
    },
  });
}

export function useAdvertiser(id: number | string) {
  return useQuery({
    queryKey: ["advertisers", id],
    queryFn: async () => {
      const res = await api.get(`/v1/advertisers/${id}`);
      if (!res.ok) throw new Error("Failed to fetch advertiser");
      return res.json();
    },
    enabled: !!id,
  });
}

export function useCreateAdvertiser() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: Record<string, unknown>) => {
      const res = await api.post("/v1/advertisers", data);
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.error || "Failed to create advertiser");
      }
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["advertisers"] });
    },
  });
}

export function useUpdateAdvertiser(id: number | string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: Record<string, unknown>) => {
      const res = await api.patch(`/v1/advertisers/${id}`, data);
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.error || "Failed to update advertiser");
      }
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["advertisers"] });
    },
  });
}

export function useDeleteAdvertiser() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: number | string) => {
      const res = await api.delete(`/v1/advertisers/${id}`);
      if (!res.ok) throw new Error("Failed to delete advertiser");
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["advertisers"] });
    },
  });
}
